import type React from 'react';
import { type JSX, useContext, useEffect, useMemo, useState } from 'react';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import FreestarAdSlot from '@freestar/pubfig-adslot-react-component';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { UserContext } from 'providers/UserProvider';
import { AdContext, defaultAdContext } from 'services/Ad';
import ArcSpan from 'services/ArcSpan';
import Script from 'next/script';
import AdManager from './AdManager';
import Bombora from 'services/Bombora';
import { shouldAlwaysRenderOutbrain } from '@/main/components/Outbrain/OutbrainConfig';

interface AdProviderProps {
  children: JSX.Element;
  pageAdConfig?: PageAdConfig;
  arcSpanPageConfig: ArcSpanPageConfig;
}

/**
 * If no consent is give, do not load ads.
 * Load ArcSpan before the ad library.
 */
const AdProvider: React.FC<AdProviderProps> = ({ children, pageAdConfig, arcSpanPageConfig }) => {
  const { hasConsent, personalizedAds } = useContext(UserContext);
  const [adContextValue, setAdContextValue] = useState(defaultAdContext);
  const [renderOutbrainScript, setRenderOutbrainScript] = useState(false);
  const [outbrainScriptLoaded, setOutbrainScriptLoaded] = useState(false);
  const [scriptKey, setScriptKey] = useState(0);
  const { hasLoggedIn } = useContext(UserContext);
  const renderAdScripts = hasConsent && pageAdConfig;
  const outbrainTestArticle = shouldAlwaysRenderOutbrain();
  // Outbrain script re-executes when personalizedAds changes
  useEffect(() => {
    if (personalizedAds === null) return;

    if (personalizedAds || outbrainTestArticle) {
      const previousScripts = document.querySelectorAll('script[src*="widgets.outbrain.com"]');
      previousScripts.forEach((script) => script.remove());
      setScriptKey((prev) => prev + 1);
      setOutbrainScriptLoaded(false);
    }

    if (!personalizedAds) {
      setAdContextValue(defaultAdContext);
    }
  }, [personalizedAds]);

  if (pageAdConfig) {
    pageAdConfig.loginStatus = hasLoggedIn ? 'logged-in' : 'guest';
  }

  const arcSpanConfig = useMemo(
    () => ({
      ...pageAdConfig,
      ...arcSpanPageConfig,
    }),
    [pageAdConfig, arcSpanPageConfig],
  );

  useEffect(() => {
    for (const [key, value] of Object.entries(arcSpanConfig)) {
      if (value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        FreestarAdSlot.setPageTargeting(key, value);
      }
    }
  }, [arcSpanConfig]);

  const memoizedAdContextValue = useMemo(
    () => ({ ...adContextValue, outbrainScriptLoaded, setRenderOutbrainScript }),
    [adContextValue, outbrainScriptLoaded],
  );

  return (
    <>
      {renderAdScripts && (
        <>
          <Bombora />
          <AdManager
            pageAdConfig={pageAdConfig}
            setAdContextValue={setAdContextValue}
          />
        </>
      )}
      <AdContext.Provider value={memoizedAdContextValue}>{children}</AdContext.Provider>
      {renderOutbrainScript && (
        <Script
          key={scriptKey}
          type='text/javascript'
          async
          strategy='afterInteractive'
          src={`//widgets.outbrain.com/outbrain.js?v=${scriptKey}`}
          onLoad={() => {
            setOutbrainScriptLoaded(true);
          }}
        />
      )}
      {process?.env?.ARCSPAN_ORGANIZATION_ID?.length && (
        <ArcSpan
          pageAdConfig={pageAdConfig}
          arcSpanPageConfig={arcSpanConfig}
        />
      )}
    </>
  );
};

export default AdProvider;
